import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AssetSummary, NewsItem } from '@/types/crypto';

export const useCryptoAssets = () => {
  return useQuery({
    queryKey: ['crypto-assets'],
    queryFn: async (): Promise<AssetSummary[]> => {
      console.log('Fetching enriched crypto assets...');
      
      // Fetch all data sources in parallel - use valuation-summary for main data
      const [valuationRes, socialRes, fundamentalsRes, alertsRes, fngRes] = await Promise.allSettled([
        supabase.functions.invoke('valuation-summary'),
        supabase.functions.invoke('social-data'),
        supabase.functions.invoke('fundamentals-data'),
        supabase
          .from('crypto_alerts')
          .select('*')
          .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()),
        supabase.functions.invoke('fear-greed-index')
      ]);

      if (valuationRes.status === 'rejected') {
        throw new Error(`Failed to fetch valuation data: ${valuationRes.reason}`);
      }

      const assets = valuationRes.value.data?.items || [];
      const socialData = socialRes.status === 'fulfilled' ? socialRes.value.data || {} : {};
      const fundamentalsData = fundamentalsRes.status === 'fulfilled' ? fundamentalsRes.value.data || {} : {};
      const alertsData = alertsRes.status === 'fulfilled' ? alertsRes.value.data || [] : [];
      const fngData = fngRes.status === 'fulfilled' ? fngRes.value.data : null;

      // Group alerts by asset_id
      const alertsMap = alertsData.reduce((acc: any, alert: any) => {
        if (!acc[alert.asset_id]) acc[alert.asset_id] = [];
        acc[alert.asset_id].push(alert);
        return acc;
      }, {});

      // Fetch derivatives data for top assets (first 20 to avoid rate limits)
      const topAssets = assets.slice(0, 20);
      const derivativesPromises = topAssets.map((asset: any) => 
        supabase.functions.invoke('derivatives-data', {
          body: { symbol: asset.symbol }
        }).catch(error => {
          console.warn(`Failed to fetch derivatives for ${asset.symbol}:`, error);
          return { data: null };
        })
      );

      const derivativesResults = await Promise.allSettled(derivativesPromises);
      
      // Create derivatives data map
      const derivativesMap: Record<string, any> = {};
      topAssets.forEach((asset: any, index: number) => {
        const result = derivativesResults[index];
        if (result.status === 'fulfilled' && result.value?.data) {
          derivativesMap[asset.symbol] = result.value.data;
        }
      });

      // Normalize data to ensure consistent types and non-null strings
      const nz = (x: unknown): number | null =>
        typeof x === "number" && Number.isFinite(x) ? x : null;
      
      const str = (x: unknown): string => 
        typeof x === "string" ? x : "";

      // Asset filtering lists (fail-safe)
      const STABLECOIN_IDS = new Set([
        'tether', 'usd-coin', 'dai', 'frax', 'usdd', 'usdp', 'fdusd', 'tusd', 'gusd', 
        'usde', 'usds', 'pyusd', 'usdn', 'usdx', 'ethena-usde', 'ethena-staked-usde',
        'binance-bridged-usdt-bnb-smart-chain', 'usdt', 'usdc'
      ]);

      const WRAPPED_TOKEN_IDS = new Set([
        'wrapped-bitcoin', 'weth', 'wrapped-beacon-eth', 'wrapped-steth', 'staked-ether',
        'coinbase-wrapped-btc', 'wrapped-steth', 'jito-staked-sol', 'wrapped-eeth',
        'binance-bridged-usdt-bnb-smart-chain'
      ]);

      const MEMECOIN_IDS = new Set([
        'dogecoin', 'shiba-inu', 'pepe', 'dogwifhat', 'floki', 'bonk', 'baby-doge-coin',
        'milady-meme-coin', 'brett-based'
      ]);

      const shouldExcludeAsset = (asset: any): boolean => {
        const id = asset.slug?.toLowerCase() || '';
        const name = asset.name?.toLowerCase() || '';
        const symbol = asset.symbol?.toLowerCase() || '';

        // Check explicit lists
        if (STABLECOIN_IDS.has(id) || WRAPPED_TOKEN_IDS.has(id) || MEMECOIN_IDS.has(id)) {
          return true;
        }

        // Stablecoin heuristics
        if (/(usd|usdt|usdc|dai|tusd|usdp|gusd|usdd|usde|usds|pyusd)$/i.test(symbol) ||
            name.includes('usd') && !name.includes('liquid')) {
          return true;
        }

        // Wrapped token heuristics
        if (id.startsWith('wrapped-') || name.includes('wrapped') || 
            name.includes('staked') && name.includes('eth')) {
          return true;
        }

        return false;
      };

      // Apply fail-safe filtering before enrichment
      const filteredAssets = assets.filter((asset: any) => !shouldExcludeAsset(asset)).slice(0, 400);
      
      // Enrich assets with additional data - use coin_gecko_id as key
      const enrichedAssets = filteredAssets.map((asset: any) => {
        // Get social data by slug (coin_gecko_id)
        const assetSocial = socialData[asset.slug] || null;
        
        return {
          ...asset,
          // Ensure strings are never undefined/null
          name: str(asset.name),
          symbol: str(asset.symbol),
          category: str(asset.category),
          // Core market data already from valuation-summary - normalized
          id: asset.slug,
          price: nz(asset.price) || 0, // Price should never be null for display
          change_1h: nz(asset.change_1h),
          change_24h: nz(asset.change_24h),
          change_7d: nz(asset.change_7d),
          market_cap: nz(asset.market_cap) || 0,
          fdv: nz(asset.fdv), // Keep null when unavailable for AI analysis
          volume_24h: nz(asset.volume_24h) || 0,
          // DeFi data - normalized
          tvl: nz(asset.tvl),
          fees_7d: nz(asset.fees_7d),
          fees_30d: nz(asset.fees_30d),
          fees_all_time: nz(asset.fees_all_time),
          fees_change_1d: nz(asset.fees_change_1d),
          revenue_7d: nz(asset.revenue_7d),
          mcap_usd: nz(asset.mcap_usd),
          defi_category: str(asset.defi_category || asset.category),
          tvl_change_7d: nz(asset.tvl_change_7d),
          tvl_change_1d: nz(asset.tvl_change_1d),
          fees_24h: nz(asset.fees_24h),
          fees_change_7d: nz(asset.fees_change_7d),
          fees_change_24h: nz(asset.fees_change_24h),
          // Social data (standardized format)
          social: assetSocial,
          social_score: null, // Deprecated LunarCrush fields
          social_mentions_change_24h: null,
          social_volume: null,
          social_dominance: null,
          galaxy_score: null,
          // Fundamentals - normalized
          revenue_30d: nz(fundamentalsData[asset.symbol]?.revenue_30d),
          revenue_change_30d: nz(fundamentalsData[asset.symbol]?.revenue_change_30d),
          active_users_30d: nz(fundamentalsData[asset.symbol]?.active_users_30d),
          pe_ratio: nz(fundamentalsData[asset.symbol]?.pe_ratio),
          ps_ratio: nz(fundamentalsData[asset.symbol]?.ps_ratio),
          // Derivatives data - normalized
          funding_rate: nz(derivativesMap[asset.symbol]?.funding_rate),
          funding_8h: nz(derivativesMap[asset.symbol]?.funding_8h),
          open_interest: nz(derivativesMap[asset.symbol]?.open_interest),
          open_interest_contracts: nz(derivativesMap[asset.symbol]?.open_interest_contracts),
          oi_change_24h: nz(derivativesMap[asset.symbol]?.oi_change_24h),
          volume_24h_futures: nz(derivativesMap[asset.symbol]?.volume_24h_futures),
          next_funding_time: nz(derivativesMap[asset.symbol]?.next_funding_time),
          // Alerts
          alerts: alertsMap[asset.slug] || [],
          // Fear & Greed for AI analysis
          fng: fngData,
          // TVL source data for AI analysis
          tvl_source: asset.tvl_source,
          tvl_source_label: asset.tvl_source_label,  
          tvl_source_key: asset.tvl_source_key,
          // Data availability flags
          has_defi_data: asset.has_tvl || asset.has_fees || asset.has_revenue,
          has_social_data: !!assetSocial,
          has_fundamentals_data: !!fundamentalsData[asset.symbol],
          has_derivatives_data: !!derivativesMap[asset.symbol],
          has_alerts: (alertsMap[asset.slug] || []).length > 0,
          // Data quality indicators
          data_quality: {
            market: true,
            defi: asset.has_tvl || asset.has_fees || asset.has_revenue,
            social: !!assetSocial,
            fundamentals: !!fundamentalsData[asset.symbol],
            derivatives: !!derivativesMap[asset.symbol],
            is_sample_data: derivativesMap[asset.symbol]?.data_source === 'sample_data'
          }
        };
      });

      // Log warnings for undefined fields in production
      if (process.env.NODE_ENV === 'production') {
        const badAsset = enrichedAssets.find((asset: any) =>
          [asset.change_24h, asset.price, asset.market_cap].some((v) => v === undefined)
        );
        if (badAsset) {
          console.warn("AlphaOnChain: critical fields undefined:", badAsset?.id);
        }
      }

      // Log coverage metrics  
      const socialCoverage = enrichedAssets.filter(asset => asset.has_social_data).length;
      const tvlCoverage = enrichedAssets.filter(asset => asset.has_defi_data).length;
      
      console.log('Enriched assets received:', enrichedAssets.length, 'assets');
      console.log('Assets with derivatives data:', Object.keys(derivativesMap).length);
      console.log(`Social coverage: ${socialCoverage}/${enrichedAssets.length} (${(socialCoverage / enrichedAssets.length * 100).toFixed(1)}%)`);
      console.log(`TVL coverage: ${tvlCoverage}/${enrichedAssets.length} (${(tvlCoverage / enrichedAssets.length * 100).toFixed(1)}%)`);
      
      // Log warnings for low coverage
      if (tvlCoverage / enrichedAssets.length < 0.5) {
        console.warn(`⚠️ Low TVL coverage: ${tvlCoverage}/${enrichedAssets.length} (${(tvlCoverage / enrichedAssets.length * 100).toFixed(1)}%) - below 50% threshold`);
      }
      if (socialCoverage / enrichedAssets.length < 0.8) {
        console.warn(`⚠️ Low social coverage: ${socialCoverage}/${enrichedAssets.length} (${(socialCoverage / enrichedAssets.length * 100).toFixed(1)}%) - below 80% threshold`);
      }
      
      return enrichedAssets;
    },
    refetchInterval: 60000,
    staleTime: 30000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
};

export const useCryptoNews = () => {
  return useQuery({
    queryKey: ['crypto-news'],
    queryFn: async (): Promise<NewsItem[]> => {
      console.log('Fetching crypto news...');
      
      const { data, error } = await supabase.functions.invoke('crypto-news');
      
      if (error) {
        console.error('News fetch error:', error);
        throw new Error(`Failed to fetch news: ${error.message}`);
      }

      return data || [];
    },
    refetchInterval: 120000, // 2 minutes
    staleTime: 60000,
    retry: 2,
  });
};

export const useDerivativesData = (symbol: string) => {
  return useQuery({
    queryKey: ['derivatives-data', symbol],
    queryFn: async () => {
      if (!symbol) return null;
      
      console.log('Fetching derivatives data for:', symbol);
      
      const { data, error } = await supabase.functions.invoke('derivatives-data', {
        body: { symbol }
      });
      
      if (error) {
        console.error('Derivatives fetch error:', error);
        throw new Error(`Failed to fetch derivatives data: ${error.message}`);
      }

      return data;
    },
    enabled: !!symbol,
    staleTime: 300000, // 5 minutes
    retry: 2,
  });
};

export const useTriggerAlertSystem = () => {
  return useQuery({
    queryKey: ['alert-system-trigger'],
    queryFn: async () => {
      console.log('Triggering alert system...');
      
      const { data, error } = await supabase.functions.invoke('alert-system');
      
      if (error) {
        console.error('Alert system error:', error);
        throw new Error(`Failed to trigger alert system: ${error.message}`);
      }

      return data;
    },
    refetchInterval: 600000, // 10 minutes
    staleTime: 300000, // 5 minutes
    retry: 1,
  });
};

export const useAssetAnalysis = (asset: AssetSummary | null) => {
  return useQuery({
    queryKey: ['asset-analysis', asset?.id],
    queryFn: async () => {
      if (!asset) return null;
      
      console.log('Fetching comprehensive AI analysis for:', asset.symbol);
      
      // Fetch historical metrics for ratio analysis
      let historicalMetrics = null;
      try {
        const { data: metricsData } = await supabase.functions.invoke('metric-history', {
          body: {
            coin_id: asset.id,
            symbol: asset.symbol,
            tvl_source_type: asset.tvl_source,
            tvl_source_key: asset.tvl_source_key,
            days: 90
          }
        });
        historicalMetrics = metricsData;
      } catch (error) {
        console.warn('Failed to fetch historical metrics:', error);
      }
      
      // Enhanced JSON payload for AI analysis
      const analysisRequest = {
        asset: asset.id || asset.symbol,
        asof: new Date().toISOString(),
        market: {
          price: asset.price,
          mcap: asset.market_cap,
          fdv: asset.fdv,
          vol24h: asset.volume_24h,
          vol30d_avg: asset.volume_24h * 30, // Approximation
          d1h: asset.change_1h || 0,
          d24h: asset.change_24h || 0,
          d7d: asset.change_7d || 0,
          rank: asset.rank,
        },
        defi: asset.tvl ? {
          tvl: asset.tvl,
          fees_7d: asset.fees_7d || 0,
          revenue_7d: asset.revenue_7d || 0,
          revenue_30d: asset.revenue_30d || 0,
          annualized: {
            fees: (asset.fees_7d || 0) * 52,
            revenue: (asset.revenue_7d || 0) * 52,
          },
          tvl_d7d: asset.tvl_change_7d || 0,
        } : null,
        derivs: (asset.funding_8h !== undefined || asset.open_interest) ? {
          funding_8h: asset.funding_8h || 0,
          oi_24h: asset.oi_change_24h || 0,
          open_interest: asset.open_interest || 0,
        } : null,
        social: asset.social ? {
          twitter: asset.social.twitter_followers || 0,
          reddit: asset.social.reddit_subscribers || 0,
          engagement_24h: asset.social.engagement_24h || 0,
        } : null,
        macro: asset.fng ? {
          fng: {
            value: asset.fng.value,
            classification: asset.fng.classification,
          }
        } : null,
        timeseries: historicalMetrics ? {
          fdv_tvl_last90: historicalMetrics.ratios.fdv_tvl || [],
          fdv_revenue_last90: historicalMetrics.ratios.fdv_revenue || [],
          tvl: historicalMetrics.tvl || [],
          revenue: historicalMetrics.revenue || [],
          price: historicalMetrics.price || []
        } : null,
        news: [], // Will be enriched by analyzer
        sources: ['CoinGecko', 'DeFiLlama', 'CryptoCompare', 'FNG', 'Binance'],
      };

      const { data, error } = await supabase.functions.invoke('crypto-analyzer', {
        body: analysisRequest,
      });

      if (error) {
        console.error('Analysis error:', error);
        throw new Error(`Failed to get analysis: ${error.message}`);
      }

      return data;
    },
    enabled: !!asset,
    staleTime: 300000, // 5 minutes
    retry: 2,
  });
};